import React from "react"
import { Link, useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"

import Layout from "../components/layout"
import SEO from "../components/seo"

const IndexPage = () => {
  const data = useStaticQuery(graphql`
    query {
      tristecGrid: file(relativePath: { eq: "tristec-grid.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 900, quality: 90) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)

  return (
    <Layout>
      <SEO
        title="Classic car stories and photography"
        description="Stories, photography and provenance from the Tristec Racing collection, including the Lamborghini Countach LP500 S and Ferrari 288 GTO Recreation."
      />

      <section className="landing-hero" aria-labelledby="home-title">
        <div>
          <p className="eyebrow">Tristec Racing collection</p>
          <h1 id="home-title">Cars with a story worth sharing.</h1>
          <p className="landing-hero__intro">
            Explore the journeys, history and photography behind two exceptional cars: a Lamborghini Countach LP500 S and a Ferrari 288 GTO Recreation.
          </p>
          <Link className="button-link" to="/tristec-gto">
            Explore the collection
          </Link>
        </div>
        <div className="landing-hero__image">
          <Img
            fluid={data.tristecGrid.childImageSharp.fluid}
            alt="The Tristec Racing collection of classic performance cars"
          />
        </div>
      </section>

      <section className="feature-grid" aria-label="Featured cars">
        <article className="feature-card">
          <p className="eyebrow">Lamborghini</p>
          <h2>Countach LP500 S</h2>
          <p>A rare UK-delivered example, with a decade of European road trips, restoration and Countach anniversaries behind it.</p>
          <Link className="button-link" to="/countach">Read the Countach story</Link>
        </article>
        <article className="feature-card">
          <p className="eyebrow">Ferrari</p>
          <h2>288 GTO Recreation</h2>
          <p>From California to the UK and the Gumball 3000: the provenance of an early Jim Carpenter recreation.</p>
          <Link className="button-link" to="/tristec-gto">Read the GTO story</Link>
        </article>
      </section>

      <section className="social-section" aria-labelledby="social-title">
        <h2 id="social-title">Follow the next chapter</h2>
        <p>More photography, events and updates from Tristec Racing.</p>
        <div className="social-links">
          <a href="https://www.instagram.com/tristecracing" target="_blank" rel="noopener noreferrer">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            Instagram
          </a>
          <a href="https://www.facebook.com/tristecracing" target="_blank" rel="noopener noreferrer">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            Facebook
          </a>
          <a href="https://www.youtube.com/@tristec" target="_blank" rel="noopener noreferrer">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            YouTube
          </a>
        </div>
      </section>
    </Layout>
  )
}

export default IndexPage
